import { Link } from "react-router-dom";

/**
 * Porté depuis apps/web/app/(shop)/confidentialite/page.tsx : `next/link` →
 * `Link` de react-router-dom (`href` → `to`), `generateMetadata` abandonné
 * (hors périmètre — cf. lot SEO/prérendu séparé).
 */
export function ConfidentialitePage() {
  return (
    <div className="container-vvs py-8 lg:py-12">
      <h1 className="font-display text-2xl font-bold text-navy sm:text-3xl">
        Politique de confidentialité
      </h1>
      <div className="mt-4 max-w-prose space-y-6 font-body text-sm leading-relaxed text-muted">
        <p>
          VVS accorde de l&apos;importance à la protection des données personnelles de ses
          clients. Cette page décrit, de façon claire, quelles données sont collectées, pourquoi,
          et quels droits vous pouvez exercer.
        </p>

        <div>
          <h2 className="font-display text-base font-semibold text-ink">Données collectées</h2>
          <p className="mt-1">
            Lors de la création de votre compte, VVS collecte votre numéro de téléphone (identifiant
            principal de votre compte) et votre adresse email, nécessaires pour créer votre compte
            et vérifier votre identité. Lors d&apos;une commande, votre nom, votre adresse de
            livraison et votre commune sont également enregistrés afin de permettre la livraison.
          </p>
        </div>

        <div>
          <h2 className="font-display text-base font-semibold text-ink">Utilisation des données</h2>
          <p className="mt-1">
            Ces informations servent exclusivement à gérer votre compte, traiter vos commandes,
            organiser la livraison et vous contacter (par téléphone ou WhatsApp) au sujet d&apos;une
            commande en cours. Votre adresse email est également utilisée pour vous envoyer un code
            de vérification lors de la connexion à votre compte.
          </p>
        </div>

        <div>
          <h2 className="font-display text-base font-semibold text-ink">Partage des données</h2>
          <p className="mt-1">
            VVS ne vend ni ne loue vos données personnelles à des tiers. Vos informations de
            livraison sont partagées avec le vendeur concerné et, le cas échéant, le service de
            livraison, dans la seule mesure nécessaire à l&apos;exécution de votre commande.
          </p>
        </div>

        <div>
          <h2 className="font-display text-base font-semibold text-ink">Conservation des données</h2>
          <p className="mt-1">
            Vos données sont conservées le temps nécessaire à la gestion de votre compte et de vos
            commandes, dans le respect des obligations légales applicables en Côte d&apos;Ivoire.
          </p>
        </div>

        <div>
          <h2 className="font-display text-base font-semibold text-ink">Minimisation des données</h2>
          <p className="mt-1">
            VVS ne demande que les informations strictement nécessaires à la création d&apos;un
            compte (téléphone, email) et à la livraison d&apos;une commande (nom, commune, adresse).
            Aucune information non nécessaire (date de naissance, genre, ou autre donnée sensible)
            n&apos;est demandée lors de l&apos;inscription ou de la commande.
          </p>
        </div>

        <div>
          <h2 className="font-display text-base font-semibold text-ink">Cookies utilisés</h2>
          <p className="mt-1">
            VVS n&apos;utilise aucun cookie de mesure d&apos;audience ni de publicité. Seuls des
            cookies strictement nécessaires au fonctionnement du site sont déposés, tous techniques
            (« httpOnly », illisibles par un script) :
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[480px] border-collapse text-left text-xs">
              <thead>
                <tr className="border-b border-line text-muted">
                  <th scope="col" className="py-2 pr-4 font-medium">Cookie</th>
                  <th scope="col" className="py-2 pr-4 font-medium">Finalité</th>
                  <th scope="col" className="py-2 font-medium">Durée</th>
                </tr>
              </thead>
              <tbody className="text-ink">
                <tr className="border-b border-line">
                  <td className="py-2 pr-4 font-mono">vvs_session_token</td>
                  <td className="py-2 pr-4">Retrouver votre panier invité d&apos;une visite à l&apos;autre</td>
                  <td className="py-2">30 jours</td>
                </tr>
                <tr className="border-b border-line">
                  <td className="py-2 pr-4 font-mono">vvs_access_token</td>
                  <td className="py-2 pr-4">Vous maintenir connecté à votre compte</td>
                  <td className="py-2">1 heure</td>
                </tr>
                <tr className="border-b border-line">
                  <td className="py-2 pr-4 font-mono">vvs_refresh_token</td>
                  <td className="py-2 pr-4">Renouveler votre connexion sans ressaisir de code</td>
                  <td className="py-2">30 jours</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-mono">vvs_customer</td>
                  <td className="py-2 pr-4">Afficher votre nom sans rappeler l&apos;API à chaque page</td>
                  <td className="py-2">30 jours</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3">
            Le détail complet est disponible sur la{" "}
            <Link to="/politique-cookies" className="text-navy underline hover:text-golddark">
              politique de cookies
            </Link>
            .
          </p>
        </div>

        <div>
          <h2 className="font-display text-base font-semibold text-ink">Vos droits</h2>
          <p className="mt-1">
            Vous pouvez demander l&apos;accès, la correction ou la suppression de vos données
            personnelles en contactant VVS via WhatsApp (voir la page Contact), en précisant votre
            numéro de téléphone ou l&apos;email associé à votre compte.
          </p>
        </div>

        <p className="border-t border-line pt-4 text-xs text-muted/80">
          Ce texte constitue un cadre général structurant, rédigé pour refléter le fonctionnement
          actuel du site. Il ne constitue pas un document juridique définitif et doit être validé
          par un professionnel du droit avant toute mise en production définitive.
        </p>
      </div>
    </div>
  );
}
